import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
} from "react";
import { Plyr } from "plyr-react";
import "plyr-react/plyr.css";

function guessVideoMime(url) {
    const path = String(url || "")
        .split("?")[0]
        .toLowerCase();
    if (path.endsWith(".mov")) {
        return "video/quicktime";
    }
    if (path.endsWith(".webm")) {
        return "video/webm";
    }
    if (path.endsWith(".m4v")) {
        return "video/x-m4v";
    }
    return "video/mp4";
}

const PLYR_OPTIONS = {
    // 关闭 Plyr 内置点击逻辑：touch + hideControls 时它只会弹出控制条，不会暂停。
    clickToPlay: false,
    hideControls: true,
    resetOnEnd: false,
    controls: [
        "play-large",
        "play",
        "progress",
        "current-time",
        "mute",
        "volume",
        "fullscreen",
    ],
};

function getReadyPlyr(plyrRef) {
    const plyr = plyrRef.current?.plyr;
    if (plyr && typeof plyr.togglePlay === "function") {
        return plyr;
    }
    return null;
}

function getMediaElement(plyrRef, videoRef) {
    const plyr = getReadyPlyr(plyrRef);
    if (plyr?.media instanceof HTMLMediaElement) {
        return plyr.media;
    }
    if (videoRef.current instanceof HTMLMediaElement) {
        return videoRef.current;
    }
    return null;
}

function isControlTarget(target) {
    if (!(target instanceof Element)) {
        return false;
    }
    return Boolean(
        target.closest(".plyr__controls") ||
            target.closest("button") ||
            target.closest('[role="slider"]') ||
            target.closest("input")
    );
}

function VideoReviewPlayer({ src, videoName, onTimeUpdate, onPause }, ref) {
    const wrapperRef = useRef(null);
    const plyrRef = useRef(null);
    const videoRef = useRef(null);
    const latestTimeMsRef = useRef(0);
    const onTimeUpdateRef = useRef(onTimeUpdate);
    const onPauseRef = useRef(onPause);

    onTimeUpdateRef.current = onTimeUpdate;
    onPauseRef.current = onPause;

    const source = useMemo(() => {
        if (!src) {
            return null;
        }
        return {
            type: "video",
            sources: [{ src, type: guessVideoMime(src) }],
        };
    }, [src]);

    const emitTime = useCallback((seconds) => {
        const ms = Math.round(Math.max(0, Number(seconds) || 0) * 1000);
        latestTimeMsRef.current = ms;
        onTimeUpdateRef.current?.(ms);
    }, []);

    const syncCurrentTime = useCallback(() => {
        const media = getMediaElement(plyrRef, videoRef);
        if (media) {
            emitTime(media.currentTime);
        }
    }, [emitTime]);

    useImperativeHandle(
        ref,
        () => ({
            getCurrentTimeMs() {
                const media = getMediaElement(plyrRef, videoRef);
                if (media && Number.isFinite(media.currentTime)) {
                    return Math.round(media.currentTime * 1000);
                }
                return latestTimeMsRef.current;
            },
            seekTo(ms) {
                const time = Math.max(0, ms) / 1000;
                const plyr = getReadyPlyr(plyrRef);
                if (plyr) {
                    plyr.currentTime = time;
                } else if (videoRef.current) {
                    videoRef.current.currentTime = time;
                }
                emitTime(time);
            },
        }),
        [emitTime]
    );

    useEffect(() => {
        latestTimeMsRef.current = 0;
        videoRef.current = null;
        onTimeUpdateRef.current?.(0);
    }, [src]);

    // Plyr 初始化后会接管 video 元素，需直接监听 plyr.media 才能拿到播放进度。
    useEffect(() => {
        if (!src) {
            return undefined;
        }

        let media = null;
        let container = null;
        let clickHandler = null;
        let syncHandler = null;
        let pauseHandler = null;
        let playHandler = null;
        let hasPlayed = false;
        let attached = false;
        let pollId = null;

        const attach = () => {
            if (attached) {
                return true;
            }

            const plyr = getReadyPlyr(plyrRef);
            if (!plyr?.media) {
                return false;
            }

            media = plyr.media;
            videoRef.current = media;
            container = wrapperRef.current?.querySelector(".plyr") ?? null;

            syncHandler = () => {
                emitTime(media.currentTime);
            };

            playHandler = () => {
                hasPlayed = true;
            };

            pauseHandler = () => {
                syncHandler();
                if (hasPlayed) {
                    onPauseRef.current?.();
                }
            };

            media.addEventListener("timeupdate", syncHandler);
            media.addEventListener("seeked", syncHandler);
            media.addEventListener("play", playHandler);
            media.addEventListener("pause", pauseHandler);
            syncHandler();

            clickHandler = (event) => {
                if (isControlTarget(event.target)) {
                    return;
                }
                plyr.togglePlay();
                window.requestAnimationFrame(syncHandler);
            };

            container?.addEventListener("click", clickHandler);
            attached = true;
            return true;
        };

        const cleanup = () => {
            if (pollId != null) {
                window.clearInterval(pollId);
                pollId = null;
            }
            if (media && syncHandler) {
                media.removeEventListener("timeupdate", syncHandler);
                media.removeEventListener("seeked", syncHandler);
            }
            if (media && playHandler) {
                media.removeEventListener("play", playHandler);
            }
            if (media && pauseHandler) {
                media.removeEventListener("pause", pauseHandler);
            }
            if (container && clickHandler) {
                container.removeEventListener("click", clickHandler);
            }
            attached = false;
            media = null;
            container = null;
            clickHandler = null;
            syncHandler = null;
            pauseHandler = null;
            playHandler = null;
        };

        if (!attach()) {
            pollId = window.setInterval(() => {
                if (attach() && pollId != null) {
                    window.clearInterval(pollId);
                    pollId = null;
                }
            }, 50);
        }

        return cleanup;
    }, [src, emitTime]);

    if (!source) {
        return null;
    }

    return (
        <div className="video-review-player" ref={wrapperRef}>
            <Plyr
                ref={plyrRef}
                source={source}
                options={PLYR_OPTIONS}
                aria-label={videoName}
                playsInline
            />
        </div>
    );
}

export default forwardRef(VideoReviewPlayer);
